<script setup>
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import {
  ref, inject, computed,
} from 'vue';
import apiMaestrosLomloe from '@/api/cifra/lomloe/maestros';
import ModalSimple from '@/components/ModalSimple.vue';
import Spinner from '@/components/Spinner.vue';

const $I18n = inject('I18n');

const showModal = ref(false);
const cursoActual = ref({});
const asignatura = ref({});
const titulo = ref(null);
const mainForm = ref(null);
const cargando = ref(false);
const competencias = ref([]);
const competenciasAnadidas = ref([]);

const emits = defineEmits(['anadir']);

const noPuedeAnadir = computed(
  () => isEmpty(competenciasAnadidas.value) || isEmpty(
    competenciasAnadidas.value.filter((ca) => !ca._destroy),
  ),
);

function byOrden(array) {
  if (isEmpty(array)) { return []; }

  return sortBy(array, (o) => o.orden);
}
async function cargarCompetencias() {
  cargando.value = true;
  try {
    const infoCursoData = (await apiMaestrosLomloe.infoCurso(cursoActual.value.id)).data;
    const asignaturaInfoCurso = infoCursoData.info_curso.asignaturas.find(
      (asg) => asg.id === asignatura.value.id,
    );
    competencias.value = byOrden(asignaturaInfoCurso.competencia_especifica_definiciones);
  } finally {
    cargando.value = false;
  }
}

function show(curso, infoAsignatura, opciones = {}) {
  titulo.value = opciones.titulo || $I18n.t('lomloe_competencias_especificas');
  cursoActual.value = curso.curso;
  asignatura.value = cloneDeep(infoAsignatura.asignatura);
  competenciasAnadidas.value = cloneDeep(infoAsignatura.competencias_especificas.filter(
    (ce) => !ce._destroy,
  ));
  competenciasAnadidas.value.forEach((ca) => {
    ca.criterios = ca.criterios.filter((criterio) => !criterio._destroy);
  });
  showModal.value = true;

  cargarCompetencias();
}

function isCriterioSeleccionado(criterioEvaluacion) {
  return competenciasAnadidas.value.flatMap(
    (compe) => compe.criterios.filter((criterio) => !criterio._destroy),
  ).some(
    (cri) => (cri.criterio_evaluacion_definicion.id === criterioEvaluacion.id),
  );
}

function cerrarModal() {
  showModal.value = false;
}

defineExpose({ show, cerrarModal });

function gestionarCriterioEvaluacion(criterioEvaluacion) {
  const nuevoCriterio = {
    criterio_evaluacion_definicion_id: criterioEvaluacion.id,
    criterio_evaluacion_definicion: criterioEvaluacion,
  };

  const existeCompetencia = competenciasAnadidas.value.find(
    // eslint-disable-next-line max-len
    (ce) => ce.competencia_especifica_definicion.id === nuevoCriterio.criterio_evaluacion_definicion.competencia_especifica_definicion_id,
  );
  if (isCriterioSeleccionado(criterioEvaluacion)) {
    existeCompetencia.criterios.find(
      (cr) => cr.criterio_evaluacion_definicion.id === criterioEvaluacion.id,
    )._destroy = true;
    if (isEmpty(existeCompetencia.criterios.filter((cr) => !cr._destroy))) {
      existeCompetencia._destroy = true;
    }
  } else if (existeCompetencia) {
    existeCompetencia.criterios.push(nuevoCriterio);
  } else {
    const competencia = competencias.value.find(
      // eslint-disable-next-line max-len
      (ced) => ced.id === nuevoCriterio.criterio_evaluacion_definicion.competencia_especifica_definicion_id,
    );

    const nuevaCompetencia = {
      competencia_especifica_definicion_id: competencia.id,
      competencia_especifica_definicion: competencia,
      criterios: [nuevoCriterio],
    };
    competenciasAnadidas.value.push(nuevaCompetencia);
  }
}
</script>
<template>
  <ModalSimple
    v-model="showModal"
    :titulo="titulo"
  >
    <div ref="mainForm">
      <Spinner v-if="cargando" :label="$t('cargando_informacion')" />
      <Transition enter-active-class="animated fadeInRight" mode="out-in">
        <div v-if="!cargando" class="asignaturas-wrapper">
          <div v-if="!isEmpty(competencias)">
            <ul
              v-for="competenciaEspecifica in competencias"
              :key="competenciaEspecifica.id"
              class="m-t-xs"
            >
              <li>
                {{ competenciaEspecifica.codigo }} - {{ competenciaEspecifica.nombre }}
              </li>
              <ul class="list-unstyled">
                <li
                  v-for="criterioEvaluacion in byOrden(
                    competenciaEspecifica.criterio_evaluacion_definiciones
                  )"
                  :key="criterioEvaluacion.id"
                  class="m-t p-l-md"
                >
                  <div class="pretty_checkbox">
                    <div class="pretty p-icon">
                      <input
                        class="pretty_checkbox"
                        type="checkbox"
                        :checked="isCriterioSeleccionado(criterioEvaluacion)"
                        @click="gestionarCriterioEvaluacion(criterioEvaluacion)"
                      >
                      <div class="state p-cifra">
                        <i class="icon fa fa-check"></i>
                        <label class="pretty_checkbox control-label"></label>
                      </div>
                    </div>
                    {{ criterioEvaluacion.codigo }} - {{ criterioEvaluacion.nombre }}
                  </div>
                </li>
              </ul>
            </ul>
          </div>
          <div v-else class="m-t">
            {{ $t('lomloe_competencias_especificas_no_hay') }}
          </div>
          <div class="form-actions m-t">
            <button
              class="btn btn-default"
              type="button"
              @click="cerrarModal"
              v-text="$t('Cancelar')"
            />
            <button
              type="button"
              class="btn btn-cifra"
              :disabled="noPuedeAnadir"
              @click="emits(
                'anadir',
                {
                  cursoId: cursoActual.id,
                  asignatura,
                  competencias: competenciasAnadidas
                }
              )"
            >
              <i class="fa fa-plus"></i> {{ $t('Anadir') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </ModalSimple>
</template>
